/***********************/
/* Angular + external */
import { ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { Subject, Subscription, debounce, timer } from "rxjs";
/***********************/
/* BFX */
// models
import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { BfxDialogCommand } from "../bfx-dialogs/models/bfx-dialog-command.enum";
import { BfxDialogData } from "../bfx-dialogs/models/bfx-dialog-data.model";
// components
// services

import { NativeDesktopSupport } from "../bfx-native-desktop/native-desktop-support";

import { LoggerService } from "@bfx/logger";
import { BfxBlotterOrdersActionService } from "app/bfx-blotter-orders/bfx-blotter-orders-action.service";
import { IActionCallout } from "app/bfx-element-library/models/CalloutModel";
import { FillNowExecutionState } from "app/bfx-order-fill-now/models/FillNowExecutionState.enum";
import {
  OrderFillNowCmd,
  OrderFillNowCmdPayload,
} from "app/bfx-order-fill-now/models/OrderFillNowCmdPayload";
import { OrderFillNowModel } from "app/bfx-order-fill-now/models/OrderFillNowModel";
import { BfxOrderFillNowService } from "app/bfx-order-fill-now/services/bfx-order-fill-now.service";
import { GoldenLayoutPopout } from "app/golden-layout/golden-layout-popout";
import { ZERO } from "shared/base-components/number-spinner.component";
import { NativeDesktopWindow } from "shared/native-desktop/NativeDesktopWindow";
const logger = LoggerService.for(__dirname);

const BUTTON_DEBOUNCE_MS = 400;
const EXT_BUTTON_DEBOUNCE_MS = 2000;

const CALLOUT_DEBOUNCE_MS = 400;
const EXT_CALLOUT_DEBOUNCE_MS = 2000;
@Component({
  selector: "bfx-order-fill-now",
  templateUrl: "./html/bfx-order-fill-now.html",
  standalone: false,
})
export class BfxOrderFillNowComponent implements OnInit, OnDestroy {
  public readonly FILL_AMOUNT_FULL: string = "full";
  public readonly FILL_AMOUNT_PARTIAL: string = "partial";

  /* DATA */
  public data = {
    title_text: "Fill Now",
    controlsDisabled: false,

    fillType: this.FILL_AMOUNT_FULL,

    fillAmount: null,
    minAmount: ZERO,
    maxAmount: null,
    remainingFillAmount: null,
    labels: {
      fillAllRemaining: "Fill All Remaining",
      fillAmount: "Fill Amount",
    },

    fixedCcy: "",

    // Used when callouts are enabled
    callout_type: "",
    callout_size: "sm",
    callout_message: "",
    callout_tooltip: "",

    dialogId: null,
    dialogEventSubject: new Subject<BfxDialogData>(),
    actionEvent: null,
    payload: null,
    dialogData: {
      show: false,
      width: 500,
    },
    body: null,

    buttons: {
      visible: {
        close: true,
        submit: true,
        edit: false,
        accept: false,
        reject: false,
      },
      enabled: {
        close: true,
        submit: false,
        edit: false,
        accept: false,
        reject: false,
      },
      labels: {
        close: "Close",
        submit: "Submit",
        edit: "Edit",
        accept: "Accept",
        reject: "Reject",
      },
    },
  };

  private order: CubiqTS.OrderDataImpl;
  private parentPopout: GoldenLayoutPopout;
  /** ID of order we're currently filling  */
  private orderId: string;

  /** Full data model for current order - includes dynamic parts of the presentation model */
  private fillNowModel: OrderFillNowModel;

  /** Subscription for incoming model change events */
  private managerSubscription: Subscription;

  private closeConfirmed: boolean = false;

  private calloutSubject: Subject<OrderFillNowModel>;
  /** Last model state when the buttons were updated - used to ensure that we only debounce when filling */
  private lastButtonUpdateState: FillNowExecutionState;
  /** Observerable for updating view buttons */
  private buttonSubject: Subject<OrderFillNowModel>;

  /** Model for readonly fillRemaining checkbox */
  public fillRemaining: boolean = true;

  constructor(
    private translate: TranslateService,
    private blotterOrderActionService: BfxBlotterOrdersActionService,
    private fillNowService: BfxOrderFillNowService,
    private element: ElementRef,
    private cd: ChangeDetectorRef,
  ) {
    // Update call out messages after period of quiet
    this.calloutSubject = this.debounceValue(
      CALLOUT_DEBOUNCE_MS,
      EXT_CALLOUT_DEBOUNCE_MS,
      (model: OrderFillNowModel) => this.updateCallout(model.actionCallout),
    );
    // Update button after period of quiet
    this.buttonSubject = this.debounceValue(BUTTON_DEBOUNCE_MS, EXT_BUTTON_DEBOUNCE_MS, (model) =>
      this.updateButtons(model),
    );
  }

  ngOnInit() {
    this.initialize();
  }

  updateCallout(actionCallout: IActionCallout): void {
    this.data.callout_type = actionCallout?.calloutType;
    this.data.callout_message = actionCallout?.calloutMessage;
    this.data.callout_tooltip = actionCallout?.calloutTooltip;
  }

  ngOnDestroy() {
    this.unsubscribeToManager();
    this.fillNowService.unsubscribeOrder(this.orderId);
    this.calloutSubject.complete();
    this.buttonSubject.complete();
  }

  protected initialize(): void {
    this.initTranslations();
    this.subscribeToManager();
  }

  glInit(initData: any, _container: any, _state: any): void {
    this.order = initData.order;
    this.orderId = this.order.orderId.toString();
    this.data.fixedCcy = this.order.fixedCode;

    const fillNowModel = this.fillNowService.lockOrderForFillNow(this.orderId, this.order);

    this.setFillNowModel(fillNowModel);
  }

  /**
   * We only allow a single window per order- if this is called with an update, then the order must be unlocked so
   * we lock again
   *
   * @param data
   */
  public glUpdate(data: any): void {
    if (data?.data?.order && data.data.order.orderId?.toString() === this.orderId) {
      this.order = data.data.order;
      this.reset();
      this.onEdit();
    } else {
      logger.out.warn(
        `ofn-c - Appears that the input order(${data?.data?.order?.orderId?.toString()}) does not match the current order id ${
          this.orderId
        }`,
      );
    }
  }

  private reset() {
    this.data.fillAmount = null;
    this.data.fillType = this.FILL_AMOUNT_FULL;
  }

  private setFillNowModel(model: OrderFillNowModel) {
    if (!model) {
      return;
    }
    if (model.orderId == this.orderId) {
      this.fillNowModel = model;
      if (model) {
        if (this.lastButtonUpdateState === FillNowExecutionState.FILLING) {
          logger.out.info(
            `ofn-c - postponing order buttons update, model state = ${
              FillNowExecutionState[model.executionState]
            }`,
          );
          this.buttonSubject.next(model);
        } else {
          this.updateButtons(model);
        }
        this.data.controlsDisabled = model.executionState !== FillNowExecutionState.LOCKED;

        this.data.maxAmount = model.maxFillAmount;
        this.data.remainingFillAmount = model.remainingFillAmount;

        // post the callout to be displayed after appropriate debounce time
        this.calloutSubject.next(model);

        this.cd.markForCheck();
      }
      this.updateSubmitButton();
    }
  }

  /**
   * Apply the button states from the input OrderFillNowModel. This can be called directly on model update, but better
   * to be called from buttonSubject when many changes are likely to occur to reduce button flashing.
   * @param model
   */
  private updateButtons(model: OrderFillNowModel) {
    if (model) {
      logger.out.info(
        `ofn-c - updating order buttons, model state = ${
          FillNowExecutionState[model.executionState]
        }`,
      );
      this.lastButtonUpdateState = model.executionState;
      this.data.buttons.visible = model.buttonVisible;
      this.data.buttons.enabled = model.buttonEnabled;
    }
  }

  /**
   * Setup model change handling from the service
   */
  private subscribeToManager(): void {
    if (this.managerSubscription) {
      this.managerSubscription.unsubscribe();
    }
    this.managerSubscription = this.fillNowService.interfaceCmd$.subscribe(
      (payload: OrderFillNowCmdPayload) => this.processEventPayload(payload),
    );
  }
  /**
   * Process a new model from the service - need to update all labels and button states
   * @param payload
   */
  private processEventPayload(payload: OrderFillNowCmdPayload) {
    logger.out.info(`ofn-c (${this.orderId}) - got payload for order ${payload.orderId} `);
    if (payload.orderId == this.orderId) {
      switch (payload.command) {
        case OrderFillNowCmd.RESET_MODEL:
          this.reset();
          break;
        case OrderFillNowCmd.UPDATE_MODEL:
          // this.updateFromModel(payload.payload);
          this.setFillNowModel(payload.payload);
          break;
        case OrderFillNowCmd.CLEAR_MODEL:
          // this.clearModel();
          break;
        case OrderFillNowCmd.ACTION_RESPONSE:
          // this.updateCallout(payload.payload);
          break;
      }
    }
  }

  private unsubscribeToManager(): void {
    this.managerSubscription.unsubscribe();
    this.managerSubscription = null;
  }

  public initTranslations() {
    this.data.title_text = this.translate.instant("fill_now.dialog.title", [this.order.orderRef]);
    this.data.labels.fillAllRemaining = this.translate.instant(
      "fill_now.dialog.fill_all_remaining",
    );
    this.data.labels.fillAmount = this.translate.instant("fill_now.dialog.fill_amount");

    this.data.buttons.labels.close = this.translate.instant("order_entry.button.close");
    this.data.buttons.labels.submit = this.translate.instant("order_entry.button.submit");
    this.data.buttons.labels.edit = this.translate.instant("order_entry.button.edit");

    this.data.buttons.labels.accept = this.translate.instant("order_entry.button.force_accept");
    this.data.buttons.labels.reject = this.translate.instant("order_entry.button.force_reject");
  }

  public onEdit() {
    const fillNowModel = this.fillNowService.lockOrderForFillNow(this.orderId, this.order);
    this.setFillNowModel(fillNowModel);
  }

  /**
   * Do a fill Now request
   */
  public onSubmit() {
    // if (this.data.fillType === this.FILL_AMOUNT_FULL) {
    this.fillNowService.fillNowFull(this.fillNowModel);
    // } else if (this.data.fillType === this.FILL_AMOUNT_PARTIAL) {
    // this.fillNowService.fillNowPartial(this.fillNowModel, this.data.fillAmount);
    // }
  }

  /**
   * Button handler for Force Accept
   */
  public onAccept(): void {
    // possible that the debounced button states are not reflecting model state so need to check
    if (this.fillNowModel.executionState === FillNowExecutionState.EXT_ACCEPTANCE) {
      this.fillNowService.showExternalWarningDialog(true).then((affirmative) => {
        if (affirmative) {
          this.fillNowModel.updateExecutionState(FillNowExecutionState.FILLING);
          this.fillNowService.forceAccept(this.fillNowModel.order);
        }
      });
    }
  }

  /**
   * Button handler for Force Reject
   */
  public onReject(): void {
    // possible that the debounced button states are not reflecting model state so need to check
    if (this.fillNowModel.executionState === FillNowExecutionState.EXT_ACCEPTANCE) {
      this.fillNowService.showExternalWarningDialog(true).then((affirmative) => {
        if (affirmative) {
          this.fillNowService.forceReject(this.fillNowModel.order);
        }
      });
    }
  }

  public onOrderAmountChange(_$event) {
    this.updateSubmitButton();
  }
  public onOrderAmountBlur(_$event) {}

  public onFillTypeChange($event) {
    this.updateSubmitButton();
  }

  /**
   * On any input change check if we have valid state for submission
   */
  private updateSubmitButton() {
    const modelSubmitEnabled = this.fillNowModel?.executionState === FillNowExecutionState.LOCKED;
    this.data.buttons.enabled.submit = modelSubmitEnabled;
    // (modelSubmitEnabled && this.data.fillType === this.FILL_AMOUNT_FULL) ||
    // (CubiqTS.BigNumber.isBigNumber(this.data.fillAmount) &&
    //   this.data.fillAmount.isGreaterThanOrEqualTo(ZERO));
  }

  setParentPopup(parentPopup: GoldenLayoutPopout) {
    this.parentPopout = parentPopup;
    this.parentPopout.setShowDock(false);
  }

  /**
   * Invoked by the close confirmation warning dialog if the
   * user chooses to go ahead and close the window
   */
  public confirmClose(): void {
    this.parentPopout.close();
  }

  /**
   * Callback that determines if this popup should be closed or not
   */
  public onClose(): boolean {
    let closePromise: Promise<boolean> = null;

    switch (this.fillNowModel.executionState) {
      case FillNowExecutionState.LOCKED:
        this.fillNowService.unlockOrderForFillNow(this.fillNowModel);
        break;
      case FillNowExecutionState.LOCKING:
      case FillNowExecutionState.FILLING:
      case FillNowExecutionState.EXT_ACCEPTANCE:
        // Put up a confirmation dialog if we're not already in the resulting callback
        if (!this.closeConfirmed) {
          closePromise = this.fillNowService.showExternalWarningDialog();
          closePromise.then((canClose) => {
            if (canClose) {
              this.closeConfirmed = true;
              this.parentPopout.close();
            }
          });
        }
        break;
      default:
        logger.out.info(
          "Not unlocking FillNow - state = " +
            FillNowExecutionState[this.fillNowModel.executionState],
        );
        break;
    }
    if (closePromise == null) {
      this.parentPopout.close();
      return true;
    } else {
      return false;
    }
  }

  /**
   *
   */
  protected openConfirmDialog(payload: any): void {
    let bfxDialogData: BfxDialogData = new BfxDialogData(
      BfxDialogCommand.DIALOG_ORDER_CANCEL_CONFIRM_OPEN,
      null,
      null,
      payload,
    );

    if (NativeDesktopSupport.isNativeDesktopContainer()) {
      let wnd: NativeDesktopWindow = NativeDesktopSupport.getWindow(this.element.nativeElement);
      bfxDialogData.body = wnd.getNativeWindow().document.body;
    }

    this.blotterOrderActionService.launchNewDialog(bfxDialogData);
  }

  /**
   * Return a subject that debounces the values before calling the specified handler
   *
   * @param debounceMs TIme to wait when not in EXTERNAL state
   * @param externalDebounceMs time to wait when in ext acceptance state
   * @param handler
   * @returns
   */
  private debounceValue(
    debounceMs: number,
    externalDebounceMs: number,
    handler: (v: OrderFillNowModel) => void,
  ): Subject<OrderFillNowModel> {
    const retSubject: Subject<OrderFillNowModel> = new Subject();
    retSubject
      .pipe(
        debounce((model: OrderFillNowModel) => {
          let debounceTime: number =
            model.executionState === FillNowExecutionState.EXT_ACCEPTANCE
              ? externalDebounceMs
              : debounceMs;
          return timer(debounceTime);
        }),
      )
      .subscribe((data: OrderFillNowModel) => handler(data));
    return retSubject;
  }
}
